import {
  apiSecurityPinBiometricV2,
  apiSecurityPinOpenVerify,
  apiSecurityPinSimpleRegistration,
} from '@api/apiCall';
import { fetchSecurityPinOpen } from '@api/useQueryCalls';
import { securityPin as pinImage } from '@assets/png/imagesPng';
import LocalImage from '@atoms/LocalImage';
import { Button, Text, View } from '@components/primitives';
import useFeatureFlag from '@customHooks/useFeatureFlag';
import LoginInfoSheet from '@molecules/LoginInfoSheet';
import navigationConstants from '@navigation/navigationConstants';
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import appStyles from '@root/appStyles';
import { FEATURE_FLAGS, SECURITY_PIN_INFO_MODAL } from '@root/constants';
import { getStoreState } from '@store';
import { getStore } from '@store/storeUtils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import colors from '@theme/colors';
import OTPInputView from '@twotalltotems/react-native-otp-input';
import { scale } from '@utils/normalize';
import { returnBottomSheetContent, showErrorOnAPI } from '@utils/utils';
// import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Keyboard, Pressable, Platform } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { logHelpClicked, logOnPage } from './analyticsCreatePiin';
import {
  MODAL_KEYS,
  getModalKeyBasedOnFeatureFlags,
  getTitleName,
  otherPinHandlers,
  phoneChangeEnrolledPinStart,
  pinHandlerFunctions,
  shoulRenderInstructions as shouldRenderInstructions,
  shouldRenderOnOpenPinError,
  shouldRenderOnVerifyOpenSuccess,
  shouldRenderOnVerifyPinError,
  shouldRenderSuccessPinCreation,
  shouldShowErrorText,
  verifyAndResetPinHandlers,
} from './pinUtils';
import styles from './styles';
import { useToast } from '@root/hooks/useToast';
import { MaterialIcons } from '@expo/vector-icons';
import { getBioTokenAsync } from '@utils/biometricUtils';
import AmplitudeHelper from '@utils/analytics';
import { AMPLITUDE_CONSTANTS } from '@utils/analytics/constants';

type RootStackParamList = {
  [navigationConstants.GG_CREATE_PIN]: {
    type: 'create';
    source?: string;
    lockedFromLaunch?: boolean;
    onVerifyCallBack?: () => void;
    onVerifyValues?: Record<string, any> | Array<any>;
  };
  [navigationConstants.GG_VERIFY_PIN]: {
    type: 'verifyPin';
    source?: string;
    lockedFromLaunch?: boolean;
    onVerifyCallBack?: () => void;
    onVerifyValues?: Record<string, any> | Array<any>;
  };
  [navigationConstants.GG_VERIFY_PIN_WITH_EXPIRY]: {
    type: 'verifyPinWithExpiry';
    source?: string;
    lockedFromLaunch?: boolean;
    onVerifyCallBack?: () => void;
    onVerifyValues?: Record<string, any> | Array<any>;
  };
};

type SecurityPinRouteProp = RouteProp<RootStackParamList>;

export interface SecurityPinOpenResponse {
  attempts: number;
  remainingAttempts: number;
  token: string;
}

const GGCreatePin: React.FC = () => {
  const [otpNew, setOtp] = useState('');
  const [confirmCopy, setConfirmCopy] = useState('');
  const [invalidConfirms, setInvalidConfirms] = useState(0);
  const [clearInputs, setClearInputs] = useState(false);
  const [errTxtMsg, setErrTxtMsg] = useState('');
  const [verifyInvalid, setVerifyInvalid] = useState(false);
  const [pinLockedMessage, setPinLockedMessage] = useState('');
  const queryClient = useQueryClient();
  const toast = useToast();

  const route = useRoute<SecurityPinRouteProp>();
  const navigation = useNavigation();
  const { t } = useTranslation('common');
  const [isFeatureAvailale] = useFeatureFlag({
    featureName: [FEATURE_FLAGS.BANK_SEL_TRNSF, FEATURE_FLAGS.PHONE_ENROLL, FEATURE_FLAGS.PHONE_NUMBER_CHANGE],
  });
  const {
    pinRegister: securityPin,
    setLoading,
    setShowPinRegister,
    securityPinOpen,
    setSecurityPinOpen,
    securityPinLocked,
    setSecurityPinLocked,
    setInitialData,
    biometricEnrolled,
  } = getStore();

  const {
    isLoading,
    isSuccess,
    isError: isErrorCreate,
    error: errorCreate,
    data,
    mutate: securityPinRegistration,
  } = useMutation({
    mutationFn: payload => apiSecurityPinSimpleRegistration(payload),
  });

  // Verify Pin with Expiry (i.e Phone Number Change, Bank & Phone Enrollment)
  const {
    isLoading: isLoadingOpenVerify,
    isSuccess: isSuccessOpenVerify,
    data: dataOpenVerify,
    isError: isErrorOpenVerify,
    error: errorOpenVerify,
    mutate: securityPinOpenVerify,
  } = useMutation({ mutationFn: payloadVerify => apiSecurityPinOpenVerify(payloadVerify) });

  const sourceParam = route.params?.source;
  let typeScreenSetter = route.params?.type;
  const { onVerifyCallBack, onVerifyValues } = route.params;
  if (route.name === navigationConstants.GG_CREATE_PIN) {
    typeScreenSetter = 'create';
  } else if (route.name === navigationConstants.GG_VERIFY_PIN_WITH_EXPIRY) {
    typeScreenSetter = 'verifyPinWithExpiry';
  }

  const [typeScreen, setTypeScreen] = useState<'create' | 'confirm' | 'verifyPinWithExpiry'>(
    typeScreenSetter,
  );

  const otpRef = useRef(null);
  const verifySuccessHandled = useRef(false);
  const [disableOTP, setDisableOTP] = useState(false);

  // Re-focus the first PIN field whenever inputs are cleared (e.g. after a failed attempt)
  // so the keypad reopens automatically without requiring a manual tap.
  useEffect(() => {
    if (!clearInputs) return;
    const timer = setTimeout(() => {
      (otpRef.current as any)?.focusField?.(0);
    }, 50);
    return () => clearTimeout(timer);
  }, [clearInputs]);

  const [infoModal, setInfoModal] = useState({
    visible: false,
    data: returnBottomSheetContent(
      SECURITY_PIN_INFO_MODAL[MODAL_KEYS.GG_01_FALLBACK],
      SECURITY_PIN_INFO_MODAL[MODAL_KEYS.GG_01_FALLBACK],
    ),
    errorCode: MODAL_KEYS.GG_01_FALLBACK,
  });

  const queryPinOpen = fetchSecurityPinOpen({});

  const { errorHandler, onCodeChangedHandler, onPrimaryPress, onPressSecondaryHandler, onCloseModal } =
    pinHandlerFunctions({
      setOtp,
      setVerifyInvalid,
      errTxtMsg,
      setErrTxtMsg,
      setClearInputs,
      route,
      navigation,
      setInfoModal,
      infoModal,
      setTypeScreen,
      MODAL_KEYS,
      onVerifyCallBack,
      onVerifyValues,
      setInvalidConfirms,
    });

  const getModalNameKey = getModalKeyBasedOnFeatureFlags(isFeatureAvailale, sourceParam)();

  const {
    verifyPinErrorHandling,
    resetPinHandler,
    verifyPinOpenHandler,
    verifyPinOpenErrorHandling,
  } = verifyAndResetPinHandlers({
    securityPin,
    setShowPinRegister,
    setVerifyInvalid,
    route,
    t,
    errorHandler,
    setInfoModal,
    setPinLockedMessage,
    securityPinOpenVerify,
    securityPinOpen,
    setSecurityPinOpen,
    setSecurityPinLocked,
    queryClient,
  });
  const { getPageNumber, onNextPress, onCodeFilledHandler } = otherPinHandlers({
    typeScreen,
    route,
    verifyPinOpenHandler,
    setDisableOTP,
    confirmCopy,
    invalidConfirms,
    setInfoModal,
    setInvalidConfirms,
    errorHandler,
    setConfirmCopy,
    otpNew,
    setTypeScreen,
    securityPinRegistration,
    t,
    securityPinOpen,
  });

  useEffect(() => {
    logOnPage(getPageNumber, route);
  }, [route.params]);

  useEffect(() => {
    setLoading(isLoading);
  }, [isLoading]);

  useEffect(() => {
    shouldRenderSuccessPinCreation(
      isSuccess,
      data,
      setInfoModal,
      getModalNameKey,
      setShowPinRegister,
      sourceParam,
      queryPinOpen,
      navigation,
    );
  }, [isSuccess, data]);

  // Handling of after Security Pin enroll before navigate to Phone Change Screen
  useEffect(() => {
    if (
      typeScreenSetter === 'create' &&
      sourceParam === 'phone_change' &&
      queryPinOpen.isSuccess &&
      queryPinOpen.data
    ) {
      const _data = queryPinOpen.data as unknown as SecurityPinOpenResponse;
      phoneChangeEnrolledPinStart(otpNew, _data?.token, securityPinOpenVerify);
    }
  }, [queryPinOpen.isSuccess, queryPinOpen.data]);

  // Verify Pin with Expiry (i.e Phone Number Change)
  useEffect(() => {
    if (queryPinOpen.isSuccess && queryPinOpen.data) {
      const _data = queryPinOpen.data as unknown as SecurityPinOpenResponse;
      setSecurityPinOpen({
        attempts: _data?.attempts,
        remainingAttempts: _data?.remainingAttempts,
        token: _data?.token,
      });
    }
    setLoading(false);
  }, [queryPinOpen.isSuccess, queryPinOpen.data]);

  useEffect(() => {
    if (typeScreenSetter === 'create' && sourceParam === 'phone_change') {
      return;
    }
    setLoading(isLoadingOpenVerify);
  }, [isLoadingOpenVerify]);

  useEffect(() => {
    showErrorOnAPI(isErrorCreate, errorCreate, toast, t, navigation, setInitialData);
  }, [isErrorCreate, errorCreate]);

  useEffect(() => {
    shouldRenderOnOpenPinError(queryPinOpen.isError, queryPinOpen.error, verifyPinOpenErrorHandling);
  }, [queryPinOpen.isError, queryPinOpen.error]);

  useEffect(() => {
    shouldRenderOnVerifyPinError(isErrorOpenVerify, errorOpenVerify, verifyPinOpenErrorHandling);
  }, [isErrorOpenVerify, errorOpenVerify]);

  // When landing on verify screen after relaunch with PIN locked (GG-SEC-PIN-09), show 30-minute lockout message
  useEffect(() => {
    const lockedFromLaunch = route.params?.lockedFromLaunch;
    if (lockedFromLaunch && typeScreen === 'verifyPinWithExpiry') {
      setPinLockedMessage(t('securityPin.pinLockedMessage'));
    }
  }, [route.params?.lockedFromLaunch, typeScreen, t]);

  // Block navigation when PIN is locked (5 failed attempts) — covers Android back, iOS swipe, and web browser back
  useEffect(() => {
    navigation.setOptions({ gestureEnabled: !securityPinLocked });
    if (!securityPinLocked) return;
    const unsubscribe = navigation.addListener('beforeRemove', e => {
      e.preventDefault();
    });
    return unsubscribe;
  }, [navigation, securityPinLocked]);

  useEffect(() => {
    if (typeScreen !== 'verifyPinWithExpiry') return;
    if (verifySuccessHandled.current) return;
    if (!isSuccessOpenVerify || !dataOpenVerify) return;
    verifySuccessHandled.current = true;
    Keyboard.dismiss();
    shouldRenderOnVerifyOpenSuccess(isSuccessOpenVerify, dataOpenVerify, route, onVerifyCallBack, setShowPinRegister, onVerifyValues, otpNew);
  }, [isSuccessOpenVerify, dataOpenVerify, typeScreen]);

  function shouldRenderButton(show) {
    if (show)
      return (
        <Button
          paddingY={scale(8)}
          testId="lanjutkanButton"
          onPress={onNextPress}
          isDisabled={disableOTP || otpNew.length < 6}>
          {t('common.continue')}
        </Button>
      );
    return null;
  }

  const onHelpHandler = () => {
    resetPinHandler('link');
    logHelpClicked();
  };

  function shouldRenderResetLink(show) {
    if (show)
      return (
        <View flex={1}>
          <View flex={1} />
          <Pressable onPress={onHelpHandler}>
            <Text variant="sm-bold" color={colors.secondary.orchid} alignSelf="center" underline>
              {t('securityPin.resetLinkText')}
            </Text>
          </Pressable>
        </View>
      );

    return null;
  }

  const shouldShowBiometricShortcut =
    Platform.OS === 'android' &&
    biometricEnrolled &&
    typeScreen === 'verifyPinWithExpiry';

  const runBiometricSuccessPath = async () => {
    setShowPinRegister({
      showPinRegister: false,
      isActive: true,
      invalidAttempts: 0,
    });
    const callback = onVerifyCallBack as ((values?: any) => void) | undefined;
    if (typeScreen === 'verifyPinWithExpiry') {
      const bioToken = await getBioTokenAsync();
      const token = getStoreState().securityPinOpen?.token;
      if (route.params?.source === 'phone_change') {
        if (token && bioToken) {
          await apiSecurityPinBiometricV2({ token, bioToken });
        }
        callback?.();
      } else {
        callback?.({ ...onVerifyValues, securityPin: null, bioToken });
      }
    }
  };

  const triggerBiometricAuth = async () => {
    if (Platform.OS !== 'android') return false;
    const fingerprintEvent = AMPLITUDE_CONSTANTS.authentication.fingerprint_auth_result;
    const source = route.params?.source ?? 'login';
    try {
      const LocalAuthentication = await import('expo-local-authentication');
      const hasHardware = await LocalAuthentication.hasHardwareAsync();
      const isEnrolled = await LocalAuthentication.isEnrolledAsync();
      if (!hasHardware || !isEnrolled) {
        AmplitudeHelper.logEvent(fingerprintEvent.name, {
          [fingerprintEvent.attributes.source]: source,
          [fingerprintEvent.attributes.status]: 'failed',
          [fingerprintEvent.attributes.fail_reason]: !hasHardware ? 'sensor_failed' : 'not_enrolled',
          [fingerprintEvent.attributes.fallback_used]: 'true',
        });
        return false;
      }

      Keyboard.dismiss();
      const result = await LocalAuthentication.authenticateAsync({
        promptMessage: t('biometricEncouragement.cta'),
        disableDeviceFallback: true,
      });
      if (result.success) {
        AmplitudeHelper.logEvent(fingerprintEvent.name, {
          [fingerprintEvent.attributes.source]: source,
          [fingerprintEvent.attributes.status]: 'success',
        });
        await runBiometricSuccessPath();
        return true;
      }

      AmplitudeHelper.logEvent(fingerprintEvent.name, {
        [fingerprintEvent.attributes.source]: source,
        [fingerprintEvent.attributes.status]: 'failed',
        [fingerprintEvent.attributes.fail_reason]: 'other_error',
        [fingerprintEvent.attributes.fallback_used]: 'true',
      });
      // Biometric failed/cancelled — refocus PIN field so keypad reopens
      setTimeout(() => {
        (otpRef.current as any)?.focusField?.(0);
      }, 100);
    } catch (_err) {
      AmplitudeHelper.logEvent(fingerprintEvent.name, {
        [fingerprintEvent.attributes.source]: source,
        [fingerprintEvent.attributes.status]: 'failed',
        [fingerprintEvent.attributes.fail_reason]: 'other_error',
        [fingerprintEvent.attributes.fallback_used]: 'true',
      });
      // Biometric error — refocus PIN field so keypad reopens
      setTimeout(() => {
        (otpRef.current as any)?.focusField?.(0);
      }, 100);
    }
    return false;
  };

  const onBiometricPress = () => {
    triggerBiometricAuth();
  };

  const hasAutoTriggeredBiometric = React.useRef(false);
  useEffect(() => {
    if (!shouldShowBiometricShortcut || hasAutoTriggeredBiometric.current) {
      return;
    }
    hasAutoTriggeredBiometric.current = true;
    const timer = setTimeout(() => {
      triggerBiometricAuth();
    }, 400);
    return () => clearTimeout(timer);
  }, [shouldShowBiometricShortcut]);

  return (
    <SafeAreaView edges={['top']} style={styles.createPinContainer}>
      <View style={appStyles.safeAreaView}>
        <LocalImage style={styles.pinImageContainer} source={pinImage} />
        <Text style={styles.pinTitleContainer} variant="lg-bold">
          {getTitleName(typeScreen, t)}
        </Text>
        <OTPInputView
          pinCount={6}
          ref={otpRef}
          code={otpNew}
          style={styles.otpInputContainer}
          clearInputs={clearInputs}
          autoFocusOnLoad={false}
          onCodeFilled={onCodeFilledHandler}
          codeInputFieldStyle={[styles.otpDefault, verifyInvalid && styles.otpFailed]}
          secureTextEntry
          onCodeChanged={onCodeChangedHandler}
          codeInputHighlightStyle={[styles.otpHightLight]}
          editable={true}
        />
        {shouldShowErrorText(errTxtMsg)}
        {pinLockedMessage ? (
          <Text variant="md-normal" style={styles.errorTextStyles}>
            {pinLockedMessage}
          </Text>
        ) : null}
        <View style={styles.instructionStyles}>
          <MaterialIcons name="info-outline" color={colors.secondary.orchid} size={24} />
          <Text variant="xsm-bold" style={appStyles.marginHorizontal(8)}>
            {t('securityPin.securityInfo')}
          </Text>
        </View>
        {shouldRenderInstructions(
          typeScreen === 'create' || typeScreen === 'confirm',
          isFeatureAvailale,
          t,
          sourceParam,
        )}
        {typeScreen === 'verifyPinWithExpiry' && (
          <Pressable onPress={onHelpHandler} style={{ marginTop: 8 }}>
            <Text variant="lg-bold" color={colors.secondary.orchid} alignSelf="center" underline>
              {t('securityPin.forgotPinLink')}
            </Text>
          </Pressable>
        )}
        {shouldShowBiometricShortcut && (
          <Pressable
            onPress={onBiometricPress}
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              marginTop: scale(24),
              marginBottom: scale(24),
            }}>
            <MaterialIcons name="fingerprint" color="black" size={24} />
            <Text variant="lg-bold" color={colors.secondary.orchid} style={appStyles.marginHorizontal(8)}>
              {t('securityPin.useBiometric')}
            </Text>
          </Pressable>
        )}
        {shouldRenderResetLink(false)}
        <LoginInfoSheet
          onPressPrimary={onPrimaryPress}
          onPressSecondary={onPressSecondaryHandler}
          loginInfo={infoModal}
          onClose={onCloseModal}
        />
      </View>
      <View style={styles.btnVerifyCreate}>
        {shouldRenderButton(typeScreen === 'create' || typeScreen === 'confirm')}
      </View>
    </SafeAreaView>
  );
};

// GGCreatePin.propTypes = {};
// GGCreatePin.defaultProps = {};
export default GGCreatePin;
